import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import Icon from 'components/Icon';
import { formatAlertTimestamp, getAlertVisuals } from './locationAlertUi';

const Card = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 14px;
  background: #fff;
  border: 1px solid #e8f1f8;
  border-left: 4px solid ${props => props.$borderColor};
  border-radius: 12px;
  padding: ${props => (props.$compact ? '14px 16px' : '16px 18px')};
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.04);
  transition: box-shadow 0.15s ease, transform 0.15s ease;

  &:hover {
    box-shadow: 0 8px 20px rgba(15, 23, 42, 0.08);
  }
`;

const IconWrap = styled.div`
  width: ${props => (props.$compact ? 36 : 40)}px;
  height: ${props => (props.$compact ? 36 : 40)}px;
  border-radius: 10px;
  background: ${props => props.$background};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const Content = styled.div`
  flex: 1;
  min-width: 0;
`;

const TitleRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
`;

const Title = styled.div`
  font-size: ${props => (props.$compact ? 15 : 16)}px;
  font-weight: 600;
  color: #1e293b;
  line-height: 1.35;
`;

const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 3px 8px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  background: ${props => props.$background};
  color: ${props => props.$color};
  white-space: nowrap;
  flex-shrink: 0;
`;

const Meta = styled.div`
  margin-top: 6px;
  font-size: 12px;
  color: #64748b;
  line-height: 1.4;
`;

const MetaStrong = styled.span`
  color: #475569;
  font-weight: 500;
`;

const LocationAlertCard = ({ alert, compact = false, showBadge = true }) => {
  const visuals = getAlertVisuals(alert.type);
  const { relative, absolute } = formatAlertTimestamp(alert.created_at);

  return (
    <Card $borderColor={visuals.borderColor} $compact={compact}>
      <IconWrap $background={visuals.iconBackground} $compact={compact}>
        <Icon name={visuals.icon} color={visuals.iconColor} fontSize={compact ? 18 : 20} />
      </IconWrap>
      <Content>
        <TitleRow>
          <Title $compact={compact}>{alert.title}</Title>
          {showBadge && (
            <Badge $background={visuals.badgeBackground} $color={visuals.badgeColor}>
              {visuals.label}
            </Badge>
          )}
        </TitleRow>
        <Meta>
          {relative && (
            <>
              <MetaStrong>{relative}</MetaStrong>
              {absolute && ` · ${absolute}`}
            </>
          )}
          {!relative && absolute}
        </Meta>
      </Content>
    </Card>
  );
};

LocationAlertCard.propTypes = {
  alert: PropTypes.shape({
    created_at: PropTypes.string,
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    title: PropTypes.string,
    type: PropTypes.string,
  }).isRequired,
  compact: PropTypes.bool,
  showBadge: PropTypes.bool,
};

export default LocationAlertCard;
